import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import config from "../config";
import Swal from "sweetalert2";
import { 
  User, Mail, Shield, Calendar, CheckCircle, XCircle, 
  Edit2, Camera, Lock, Save, X, Upload, Image as ImageIcon,
  Phone, MapPin, Briefcase, Users, Clock, Award
} from "lucide-react";

/* ================= HELPERS ================= */

const createImage = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
  });

const getCroppedImg = async (imageSrc, crop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg");
  });
};

/* ================= COMPONENT ================= */

const ViewEditProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [coverPicUrl, setCoverPicUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("about");

  /* Password modal */
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  /* Edit profile modal */
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});

  /* Image crop for profile */
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /* Cover photo crop */
  const [coverSrc, setCoverSrc] = useState(null);
  const [coverCrop, setCoverCrop] = useState({ x: 0, y: 0 });
  const [coverZoom, setCoverZoom] = useState(1);
  const [coverCroppedPixels, setCoverCroppedPixels] = useState(null);
  const [showCoverCrop, setShowCoverCrop] = useState(false);

  /* ================= LOAD USER ================= */

  useEffect(() => {
    const storedUser = localStorage.getItem("admin");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setEditData(parsed);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.username) return;

    const fetchProfilePicture = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Admin/get-profile-picture/${user.username}`
        );
        if (res.ok) {
          const blob = await res.blob();
          setProfilePicUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error("Profile picture fetch error:", err);
      }
    };

    const fetchCoverPicture = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Admin/get-cover-picture/${user.username}`
        );
        if (res.ok) {
          const blob = await res.blob();
          setCoverPicUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error("Cover picture fetch error:", err);
        // Cover picture is optional, so no error shown
      }
    };

    fetchProfilePicture();
    fetchCoverPicture();
  }, [user?.username]);

  /* ================= HANDLERS ================= */

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      return Swal.fire({
        icon: "warning",
        title: "⚠️ Missing Fields",
        text: "Please fill in both password fields",
        confirmButtonColor: "#6366f1",
      });
    }

    if (newPassword !== confirmPassword) {
      return Swal.fire({
        icon: "error",
        title: "❌ Passwords Don't Match",
        text: "Please make sure both passwords are the same",
        confirmButtonColor: "#6366f1",
      });
    }

    if (newPassword.length < 6) {
      return Swal.fire({
        icon: "warning",
        title: "⚠️ Password Too Short",
        text: "Password must be at least 6 characters",
        confirmButtonColor: "#6366f1",
      });
    }

    // Show loading
    Swal.fire({
      title: "⏳ Changing Password...",
      html: "Please wait...",
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Admin/change-password/${user.username}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newPassword, confirmPassword }),
        }
      );

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || "Failed to change password");
      }

      await Swal.fire({
        icon: "success",
        title: "✅ Success!",
        text: "Password changed successfully",
        confirmButtonColor: "#6366f1",
        timer: 2000,
      });

      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "❌ Error",
        text: error.message || "Server error occurred",
        confirmButtonColor: "#6366f1",
      });
    }
  };

const handleSaveProfile = async () => {
  Swal.fire({
    title: "⏳ Updating Profile...",
    html: `
      <div style="width:100%; background:#eee; border-radius:6px; height:10px; margin-top:10px;">
        <div id="profile-progress"
          style="width:0%; height:100%;
          background:linear-gradient(90deg,#6366f1,#8b5cf6);
          border-radius:6px; transition:width .3s;"></div>
      </div>
      <p style="margin-top:8px; font-size:14px;">Please wait...</p>
    `,
    allowOutsideClick: false,
    showConfirmButton: false,
  });

  const progressBar =
    Swal.getHtmlContainer()?.querySelector("#profile-progress");

  try {
    /* ================= STEP 1: UPDATE TEXT INFO ================= */
    if (progressBar) progressBar.style.width = "30%";

    const updateRes = await fetch(
      `${config.API_BASE_URL}/Admin/update-profile/${user.username}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullNames: editData.fullNames,
          email: editData.email,
          team: editData.team,
          roleTeams: editData.roleTeams,
        }),
      }
    );

    if (!updateRes.ok) {
      const err = await updateRes.json();
      throw new Error(err.message || "Profile update failed");
    }

    /* ================= STEP 2: UPLOAD PROFILE PICTURE ================= */
    if (imageSrc && croppedAreaPixels) {
      if (progressBar) progressBar.style.width = "70%";

      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append("file", blob, "profile.png");

      const picRes = await fetch(
        `${config.API_BASE_URL}/Admin/upload-profile-picture?username=${user.username}`,
        {
          method: "POST",
          body: formData, // ❗ NO headers
        }
      );

      if (!picRes.ok) {
        const err = await picRes.json();
        throw new Error(err.message || "Profile picture upload failed");
      }
    }

    /* ================= COMPLETE ================= */
    if (progressBar) progressBar.style.width = "100%";

    const updatedUser = { ...user, ...editData };
    localStorage.setItem("admin", JSON.stringify(updatedUser));

    await Swal.fire({
      icon: "success",
      title: "✅ Success!",
      text: "Profile updated successfully!",
      confirmButtonColor: "#6366f1",
      timer: 1800,
    });

    window.location.reload();
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "❌ Update Failed",
      text: error.message || "Something went wrong",
      confirmButtonColor: "#6366f1",
    });
  }
};

  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setCoverSrc(reader.result);
      setShowCoverCrop(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCover = async () => {
    if (!coverSrc || !coverCroppedPixels) {
      return Swal.fire({
        icon: "warning",
        title: "⚠️ No Image Selected",
        text: "Please select and crop an image first",
        confirmButtonColor: "#6366f1",
      });
    }

    Swal.fire({
      title: "⏳ Uploading Cover...",
      html: "Please wait...",
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const blob = await getCroppedImg(coverSrc, coverCroppedPixels);
      const formData = new FormData();
      formData.append("file", blob, "cover.jpg");
      formData.append("username", user.username);

      const res = await fetch(
        `${config.API_BASE_URL}/Admin/upload-cover-picture`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Cover upload failed");
      }

      await Swal.fire({
        icon: "success",
        title: "✅ Success!",
        text: "Cover photo updated!",
        confirmButtonColor: "#6366f1",
        timer: 2000,
      });

      setShowCoverCrop(false);
      window.location.reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "❌ Upload Failed",
        text: error.message || "Failed to upload cover photo",
        confirmButtonColor: "#6366f1",
      });
    }
  };

  /* ================= UI ================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pb-10">
      
      {/* COVER PHOTO */}
      <div className="relative h-80 bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-600 overflow-hidden group">
        {coverPicUrl ? (
          <img 
            src={coverPicUrl} 
            className="w-full h-full object-cover"
            alt="Cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-600" />
        )}
        
        {/* Upload Cover Button */}
        <label className="absolute bottom-4 right-4 px-4 py-2 bg-gray-900/70 backdrop-blur-md text-white rounded-lg cursor-pointer hover:bg-gray-900/90 transition-all flex items-center gap-2 opacity-0 group-hover:opacity-100">
          <Camera className="w-4 h-4" />
          Change Cover
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleCoverUpload}
            className="hidden"
          />
        </label>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/50" />
      </div>

      {/* PROFILE SECTION */}
      <div className="max-w-6xl mx-auto px-4 -mt-20 relative z-10">
        <div className="bg-gray-800/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 overflow-hidden">
          
          {/* Profile Header */}
          <div className="relative p-8">
            {/* Profile Picture */}
            <div className="flex flex-col md:flex-row items-center md:items-end gap-6">
              <div className="relative group">
                <img
                  src={profilePicUrl || "https://ui-avatars.com/api/?name=" + (user?.fullNames || user?.username) + "&size=200&background=6366f1&color=fff"}
                  className="w-40 h-40 rounded-full border-8 border-gray-800 object-cover shadow-2xl ring-4 ring-indigo-500/50"
                  alt="Profile"
                />
                
                {/* Change Profile Picture Button */}
                <label className="absolute bottom-2 right-2 p-2 bg-indigo-600 rounded-full cursor-pointer hover:bg-indigo-700 transition-all shadow-lg">
                  <Camera className="w-5 h-5 text-white" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => setImageSrc(reader.result);
                      reader.readAsDataURL(file);
                      setShowEditModal(true);
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Name and Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-4xl font-bold text-white mb-2">
                  {user?.fullNames || user?.username}
                </h1>
                <p className="text-lg text-gray-400 mb-3">@{user?.username}</p>
                
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="px-4 py-2 bg-indigo-600/20 text-indigo-300 rounded-full text-sm font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {user?.role}
                  </span>
                  <span className="px-4 py-2 bg-blue-600/20 text-blue-300 rounded-full text-sm font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {user?.team || "No Team"}
                  </span>
                  {user?.isApproved ? (
                    <span className="px-4 py-2 bg-green-600/20 text-green-300 rounded-full text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Approved
                    </span>
                  ) : (
                    <span className="px-4 py-2 bg-red-600/20 text-red-300 rounded-full text-sm font-medium flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Pending Approval
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all flex items-center gap-2 shadow-lg hover:shadow-indigo-500/50"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Profile
                </button>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-all flex items-center gap-2 shadow-lg"
                >
                  <Lock className="w-4 h-4" />
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-t border-gray-700/50 px-8">
            <div className="flex gap-8">
              <TabButton 
                active={activeTab === "about"} 
                onClick={() => setActiveTab("about")}
                icon={<User className="w-4 h-4" />}
              >
                About
              </TabButton>
              <TabButton 
                active={activeTab === "permissions"} 
                onClick={() => setActiveTab("permissions")}
                icon={<Shield className="w-4 h-4" />}
              >
                Permissions
              </TabButton>
              <TabButton 
                active={activeTab === "activity"} 
                onClick={() => setActiveTab("activity")}
                icon={<Clock className="w-4 h-4" />}
              >
                Activity
              </TabButton>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === "about" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoCard icon={<Mail />} label="Email" value={user?.email} color="blue" />
                <InfoCard icon={<Shield />} label="Role" value={user?.role} color="blue" />
                <InfoCard icon={<Users />} label="Team" value={user?.team} color="blue" />
                <InfoCard icon={<Award />} label="Role in Team" value={user?.roleTeams} color="blue" />
                <InfoCard icon={<User />} label="ID Code" value={user?.identificationCode} color="blue" />
                <InfoCard 
                  icon={<Calendar />} 
                  label="Registration Date" 
                  value={user?.registrationDate ? new Date(user.registrationDate).toLocaleDateString() : "N/A"} 
                  color="blue" 
                />
                <InfoCard 
                  icon={<Clock />} 
                  label="Last Modified" 
                  value={user?.modifiedDate ? new Date(user.modifiedDate).toLocaleDateString() : "N/A"} 
                  color="blue" 
                />
                <InfoCard 
                  icon={user?.isApproved ? <CheckCircle /> : <XCircle />} 
                  label="Account Status" 
                  value={user?.isApproved ? "Approved" : "Pending"} 
                  color={user?.isApproved ? "green" : "red"} 
                />
              </div>
            )}

            {activeTab === "permissions" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <PermissionCard label="Chat" enabled={user?.chatPermission} />
                <PermissionCard label="Notifications" enabled={user?.notificationPermission} />
                <PermissionCard label="Add User" enabled={user?.addUserPermission} />
                <PermissionCard label="Add Admin" enabled={user?.addAdminPermission} />
                <PermissionCard label="Tasks" enabled={user?.taskPermission} />
                <PermissionCard label="Incidents" enabled={user?.incidentPermission} />
                <PermissionCard label="Calendar" enabled={user?.calendarPermission} />
                <PermissionCard label="Ticket List" enabled={user?.ticketlistPermission} />
                <PermissionCard label="Gallery" enabled={user?.galleryPermission} />
                <PermissionCard label="Assignees" enabled={user?.assigneensPermission} />
                <PermissionCard label="Workload" enabled={user?.workloadPermission} />
                <PermissionCard label="Email" enabled={user?.emailPermission} />
                <PermissionCard label="On-Call" enabled={user?.oncallPermission} />
              </div>
            )}

            {activeTab === "activity" && (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">Activity Log</h3>
                <p className="text-gray-500">Activity tracking coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= EDIT MODAL ================= */}
      {showEditModal && (
        <Modal title="Edit Profile" onClose={() => {
          setShowEditModal(false);
          setImageSrc(null);
        }}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
            
            {/* Profile Picture Cropper */}
            {imageSrc && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Profile Picture</label>
                <div className="relative h-64 bg-black rounded-lg overflow-hidden">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                  />
                </div>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            )}

            {!imageSrc && (
              <label className="block">
                <span className="text-sm font-medium text-gray-300">Upload Profile Picture</span>
                <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-lg hover:border-indigo-500 transition-colors cursor-pointer">
                  <div className="space-y-1 text-center">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="text-sm text-gray-400">
                      <span className="text-indigo-400">Upload a file</span> or drag and drop
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => setImageSrc(reader.result);
                      reader.readAsDataURL(file);
                    }}
                  />
                </div>
              </label>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                <input
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={editData.fullNames || ""}
                  onChange={(e) => setEditData({ ...editData, fullNames: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={editData.email || ""}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Team</label>
                <input
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={editData.team || ""}
                  onChange={(e) => setEditData({ ...editData, team: e.target.value })}
                  placeholder="Enter team"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role in Team</label>
                <input
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={editData.roleTeams || ""}
                  onChange={(e) => setEditData({ ...editData, roleTeams: e.target.value })}
                  placeholder="Enter role"
                />
              </div>
            </div>

            <button 
              onClick={handleSaveProfile} 
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all flex items-center justify-center gap-2 font-medium shadow-lg"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* ================= COVER CROP MODAL ================= */}
      {showCoverCrop && (
        <Modal title="Adjust Cover Photo" onClose={() => setShowCoverCrop(false)}>
          <div className="space-y-4">
            <div className="relative h-64 bg-black rounded-lg overflow-hidden">
              <Cropper
                image={coverSrc}
                crop={coverCrop}
                zoom={coverZoom}
                aspect={16 / 9}
                onCropChange={setCoverCrop}
                onZoomChange={setCoverZoom}
                onCropComplete={(_, pixels) => setCoverCroppedPixels(pixels)}
              />
            </div>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={coverZoom}
              onChange={(e) => setCoverZoom(parseFloat(e.target.value))}
              className="w-full"
            />
            <button 
              onClick={handleSaveCover}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Cover Photo
            </button>
          </div>
        </Modal>
      )}

      {/* ================= PASSWORD MODAL ================= */}
      {showPasswordModal && (
        <Modal title="Change Password" onClose={() => setShowPasswordModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
              <input
                type="password"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
              <input
                type="password"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <button 
              onClick={handleChangePassword}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all flex items-center justify-center gap-2 font-medium"
            >
              <Lock className="w-5 h-5" />
              Change Password
            </button>
          </div>
        </Modal>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6366f1;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

/* ================= REUSABLE COMPONENTS ================= */

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-gray-800 rounded-2xl w-full max-w-3xl shadow-2xl border border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  </div>
);

const InfoCard = ({ icon, label, value, color = "indigo" }) => {
  const colorClasses = {
    indigo: "from-indigo-600/20 to-indigo-600/5 border-indigo-500/30 text-indigo-300",
    blue: "from-blue-600/20 to-blue-600/5 border-blue-500/30 text-blue-300",
    blue: "from-blue-600/20 to-blue-600/5 border-blue-500/30 text-blue-300",
    green: "from-green-600/20 to-green-600/5 border-green-500/30 text-green-300",
    blue: "from-blue-600/20 to-blue-600/5 border-blue-500/30 text-blue-300",
    orange: "from-orange-600/20 to-orange-600/5 border-orange-500/30 text-orange-300",
    cyan: "from-cyan-600/20 to-cyan-600/5 border-cyan-500/30 text-cyan-300",
    red: "from-red-600/20 to-red-600/5 border-red-500/30 text-red-300",
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-5 hover:scale-105 transition-transform`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-white/10 rounded-lg">
          {React.cloneElement(icon, { className: "w-5 h-5" })}
        </div>
        <p className="text-sm font-medium text-gray-400">{label}</p>
      </div>
      <p className="text-lg font-semibold text-white truncate">{value || "-"}</p>
    </div>
  );
};

const PermissionCard = ({ label, enabled }) => (
  <div className={`p-4 rounded-xl border-2 transition-all ${
    enabled 
      ? "bg-green-600/10 border-green-500/50 hover:bg-green-600/20" 
      : "bg-gray-700/30 border-gray-600/50 hover:bg-gray-700/50"
  }`}>
    <div className="flex items-center justify-between">
      <span className="font-medium text-white">{label}</span>
      {enabled ? (
        <CheckCircle className="w-5 h-5 text-green-400" />
      ) : (
        <XCircle className="w-5 h-5 text-gray-500" />
      )}
    </div>
  </div>
);

const TabButton = ({ active, onClick, children, icon }) => (
  <button
    onClick={onClick}
    className={`py-4 px-2 border-b-2 font-medium transition-all flex items-center gap-2 ${
      active
        ? "border-indigo-500 text-indigo-400"
        : "border-transparent text-gray-400 hover:text-gray-300"
    }`}
  >
    {icon}
    {children}
  </button>
);

export default ViewEditProfile;